/**
 * 订单详情抽屉 - 付款记录区块
 *
 * 展示付款方式（含订单抵消特殊样式）、付款日期、已收金额与凭证图预览
 * （凭证经授权拉取为 Blob 后本地渲染，令牌不进 URL）；支持内联切换为快速收款表单（金额/方式/日期/
 * 凭证上传预览）。受控组件，表单状态与上传保存回调均由父组件编排。
 */
import type { ChangeEvent, RefObject } from 'react';
import { useProtectedUploadUrl } from '../../../../../hooks/useProtectedUploadUrl';
import { fmtDate } from '../utils';
import { PAYMENT_METHODS } from '../../../constants';
import { pendingBalanceOf } from '../../../../../utils/paymentAmount';
import InfoRow from '../InfoRow';

interface Props {
  orderAmount: number | null;
  /** 这张单有没有留下过收款痕迹（实收/方式/日期/凭证任一有值）：没有就不摆「已收 ¥0.00 + 方式 -」 */
  hasPaymentRecord: boolean;
  /** 已取消（非审核拒绝）的单不再收款；已完成/已归档仍可结算提交，所以不能用 canEdit 当这里的判据 */
  isCancelled: boolean;
  /** 建单方：付款流水只有归属方能记，客户方连收款入口都不该看到（后端 payment-record.create 同源） */
  isOwner: boolean;
  localPayMethod: string | null;
  localPayDate: string | null;
  localPaidAmount: number | null;
  localVoucherUrl: string | null;
  computedPayStatus: string;
  showPayForm: boolean;
  payAmount: string;
  payMethod: string;
  payDate: string;
  payPreview: string | null;
  payFile: File | null;
  payError: string;
  /** 填了金额却没选方式：这一格单独标红，别混进整块 payError 让人猜是哪格没填 */
  payMethodError: string;
  payUploading: boolean;
  fileInputRef: RefObject<HTMLInputElement>;
  onOpenForm: () => void;
  onCancelForm: () => void;
  onSave: () => void;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onPayAmountChange: (value: string) => void;
  onPayMethodChange: (value: string) => void;
  onPayDateChange: (value: string) => void;
  onClearFile: () => void;
  onPickFile: () => void;
}

export default function PaymentSection({
  orderAmount,
  hasPaymentRecord,
  isCancelled,
  isOwner,
  localPayMethod,
  localPayDate,
  localPaidAmount,
  localVoucherUrl,
  computedPayStatus,
  showPayForm,
  payAmount,
  payMethod,
  payDate,
  payPreview,
  payFile,
  payError,
  payMethodError,
  payUploading,
  fileInputRef,
  onOpenForm,
  onCancelForm,
  onSave,
  onFileChange,
  onPayAmountChange,
  onPayMethodChange,
  onPayDateChange,
  onClearFile,
  onPickFile,
}: Props) {
  const voucherSrc = useProtectedUploadUrl(localVoucherUrl);
  const money = (v: number | null) => '¥' + Number(v ?? 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 });
  return (
    <>
      {/* Payment */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">付款记录</p>
          {!showPayForm && isOwner && !isCancelled && !localPayMethod?.includes('订单抵消') && (
            <button onClick={onOpenForm} className="flex items-center gap-1 text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-2-line text-sm" /></div>
              记录收款
            </button>
          )}
        </div>
        {!showPayForm && (
          <div className="bg-gray-50 rounded-xl p-4">
            {!hasPaymentRecord ? (
              /* 一分没收的单：这里说的是「这张单多少钱、还差多少」，不是「已收 ¥0.00 + 方式 -」
                 （裁定 2026-10-02：那种摆法看着像数据丢了，实际什么都没发生） */
              <>
                <InfoRow icon="ri-money-cny-circle-line" label="订单金额" value={money(orderAmount)} />
                <InfoRow
                  icon="ri-time-line"
                  label="未收款"
                  value={money(pendingBalanceOf(Number(orderAmount ?? 0), Number(localPaidAmount ?? 0)))}
                />
              </>
            ) : (
              <>
                {localPayMethod?.includes('订单抵消') ? (
                  <div className="flex items-start gap-2.5 py-2 border-b border-gray-50">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-exchange-dollar-line text-sm text-teal-500" />
                    </div>
                    <span className="text-xs text-gray-400 w-16 shrink-0 pt-0.5">付款方式</span>
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-teal-50 text-teal-600 border border-teal-200 rounded-full font-medium shadow-sm">
                      <i className="ri-exchange-dollar-line text-xs" />
                      {localPayMethod}
                      <span className="text-[10px] text-teal-400 font-normal">· 对冲结算</span>
                    </span>
                  </div>
                ) : (
                  <InfoRow icon="ri-bank-card-line" label="付款方式" value={localPayMethod ?? '-'} />
                )}
                <InfoRow icon="ri-calendar-2-line" label="付款日期" value={localPayDate ? fmtDate(localPayDate) : '-'} />
                <InfoRow icon="ri-money-cny-circle-line" label="已收金额" value={money(localPaidAmount)} />
              </>
            )}
            {localVoucherUrl && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-400 mb-1 flex items-center gap-1.5"><i className="ri-image-line text-gray-400" />{computedPayStatus === '已结清' ? '结清凭证' : '收款凭证'}</p>
                {voucherSrc && (
                  <a href={voucherSrc} target="_blank" rel="noopener noreferrer" className="block group relative rounded-lg overflow-hidden border border-gray-200 cursor-pointer">
                    <img src={voucherSrc} alt="付款凭证" className="w-full max-h-52 object-contain bg-gray-100" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="bg-white/90 rounded-lg px-3 py-1.5 flex items-center gap-1.5 text-xs text-gray-700"><i className="ri-zoom-in-line text-sm" />点击查看大图</div>
                    </div>
                  </a>
                )}
              </div>
            )}
          </div>
        )}
        {showPayForm && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-medium text-teal-700 flex items-center gap-1.5"><i className="ri-edit-2-line text-sm" />快速更新收款信息</p>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">已收金额（元）</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">¥</span>
                <input type="number" min="0" step="0.01" value={payAmount} onChange={e => onPayAmountChange(e.target.value)} placeholder="0.00" className="w-full pl-7 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all" />
              </div>
              {orderAmount && (
                <p className="text-xs text-gray-400 mt-1">
                  订单总额 ¥{Number(orderAmount).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}，
                  余款 ¥{pendingBalanceOf(Number(orderAmount), parseFloat(payAmount || '0')).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">付款方式</label>
              <input list="pay-methods-list" type="text" value={payMethod} onChange={e => onPayMethodChange(e.target.value)} placeholder="选择或输入付款方式" className={`w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 transition-all ${payMethodError ? 'border-rose-400' : 'border-gray-200 focus:border-teal-400'}`} />
              <datalist id="pay-methods-list">{PAYMENT_METHODS.map(m => <option key={m} value={m} />)}</datalist>
              {payMethodError && <p className="text-xs text-rose-500 mt-1 flex items-center gap-1"><i className="ri-error-warning-line text-sm" />{payMethodError}</p>}
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">收款日期</label>
              <input type="date" value={payDate} onChange={e => onPayDateChange(e.target.value)} className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all text-gray-700" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">收款凭证（可选）</label>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" />
              {payPreview ? (
                <div className="relative rounded-lg overflow-hidden border border-teal-200">
                  <img src={payPreview} alt="预览" className="w-full max-h-40 object-contain bg-gray-50" />
                  <button onClick={onClearFile} className="absolute top-2 right-2 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-50 transition-colors">
                    <i className="ri-close-line text-xs text-gray-500" />
                  </button>
                  <div className="px-2 py-1.5 bg-white border-t border-teal-100"><p className="text-xs text-gray-400 truncate">{payFile?.name}</p></div>
                </div>
              ) : (
                <button onClick={onPickFile} className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-teal-200 rounded-lg text-sm text-teal-500 hover:bg-teal-50 cursor-pointer transition-colors">
                  <i className="ri-upload-cloud-line text-base" />{localVoucherUrl ? '重新上传凭证' : '上传收款凭证'}
                </button>
              )}
              {localVoucherUrl && !payPreview && <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><i className="ri-image-2-line text-xs" />已有凭证，上传新图片将替换原凭证</p>}
            </div>
            {payError && <p className="text-xs text-rose-500 flex items-center gap-1"><i className="ri-error-warning-line text-sm" />{payError}</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={onCancelForm} disabled={payUploading} className="flex-1 py-2 text-sm border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors bg-white">取消</button>
              <button onClick={onSave} disabled={payUploading} className="flex-1 py-2 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
                {payUploading ? <><i className="ri-loader-4-line animate-spin text-sm" />保存中...</> : <><i className="ri-check-line text-sm" />确认保存</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

